import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ClipboardList, Plus } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Modal from '../components/Modal';
import { FF, FS, ErrMsg } from '../components/FormField';
import { FilterBar, SearchInput, FilterSelect, PageHeader } from '../components/FilterBar';
import DateRangePicker from '../components/DateRangePicker';
import { WARRANTY_PERIODS } from '../utils/warranty';
import CompanyDeptFilter from '../components/CompanyDeptFilter';
import SearchableSelect from '../components/SearchableSelect';
import EventTimeline from '../components/EventTimeline';
import BoardApprovalPanel, { BoardApprovalChip } from '../components/BoardApproval';

const REQUEST_ITEMS = [
  'Mobile Tempered Glass','Mobile Back Cover','Laptop Power Adapter','Laptop Power Cable',
  'Mobile Phone Charger Cable','Mobile Phone Power Adapter','Wired Mouse','Wireless Mouse','USB Drive'
];

const STATUSES = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'pending_board_approval', label: 'Pending Board Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected_by_it', label: 'Rejected by IT' },
  { value: 'rejected_by_board', label: 'Rejected by Board' },
  { value: 'completed', label: 'Completed' },
];

const STATUS_COLORS = {
  submitted: '#f59e0b', pending_board_approval: '#818cf8', approved: '#34d399',
  rejected_by_it: '#ef4444', rejected_by_board: '#ef4444', completed: '#10b981',
};

function StatusBadge({ status }) {
  const s = STATUSES.find(x => x.value === status);
  const color = STATUS_COLORS[status] || '#475569';
  return (
    <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>
      {s?.label || status}
    </span>
  );
}

export default function AccessoryRequests() {
  const { isIT, isAdmin, isJuniorIT } = useAuth();
  const canUpdateStatus = isAdmin || (isIT && !isJuniorIT); // backend canEdit excludes junior_it
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');
  const [filterCompany, setFilterCompany] = useState([]); const [filterDept, setFilterDept] = useState(null);
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [statusForm, setStatusForm] = useState({});
  const [addToAccessory, setAddToAccessory] = useState(false);
  const [accForm, setAccForm] = useState({});
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [obForm, setObForm] = useState({ on_behalf: true, employee_id: '', items: [], other: '', showOther: false, reason: '' });
  const [obError, setObError] = useState('');
  const [obSaving, setObSaving] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const refreshTimeline = (id) => {
    setTimelineLoading(true);
    api.get(`/accessory-requests/${id}/timeline`)
      .then(x => setTimeline(x.data || [])).catch(() => {}).finally(() => setTimelineLoading(false));
  };

  const submitOnBehalf = async (e) => {
    e.preventDefault(); setObError('');
    if (obForm.on_behalf && !obForm.employee_id) { setObError('Select the employee this request is for, or switch to a direct IT request.'); return; }
    if (!obForm.items.length && !obForm.other?.trim()) { setObError('Select at least one item or describe in Other.'); return; }
    if (!obForm.reason?.trim()) { setObError('Reason is required.'); return; }
    setObSaving(true);
    try {
      await api.post('/accessory-requests', {
        on_behalf: obForm.on_behalf,
        employee_id: obForm.on_behalf ? obForm.employee_id : undefined,
        requested_items: obForm.items,
        other_item: obForm.other?.trim() || undefined,
        reason: obForm.reason,
      });
      setModal(null); load();
    } catch (err) { setObError(err.response?.data?.error || 'Submission failed'); }
    finally { setObSaving(false); }
  };
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [boardInfo, setBoardInfo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, search: search || undefined, status: filterStatus || undefined, from: dateFrom || undefined, to: dateTo || undefined };
      if (filterCompany.length) params.company_id = filterCompany.join(',');
      if (filterDept) params.department_id = filterDept;
      const r = await api.get('/accessory-requests', { params });
      setRows(r.data.data || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, search, filterStatus, dateFrom, dateTo, filterCompany, filterDept]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/master/accessory-categories').then(r => setCategories(r.data || [])).catch(() => {});
    api.get('/employees', { params: { limit: 1000, status: 'active' } }).then(r => setEmployees(r.data.data || [])).catch(() => {});
  }, []);

  const openDetail = (r) => {
    setSelected(r);
    setStatusForm({ status: r.status, it_notes: r.it_notes || '', rejection_reason: r.rejection_reason || '', approved_by: r.approved_by || '' });
    setAddToAccessory(false);
    setAccForm({ quantity: 1, status: 'available', purchased_date: new Date().toISOString().split('T')[0], warranty_period: '1_year' });
    setError('');
    setBoardInfo(null);
    setModal('detail');
    api.get(`/accessory-requests/${r.id}/board-approval`).then(x => setBoardInfo(x.data)).catch(() => {});
    setTimeline([]); refreshTimeline(r.id);
  };

  const handleUpdateStatus = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const creatingAccessory = addToAccessory && statusForm.status === 'completed';
      let linkedAccessoryId;

      // Validate and create the accessory BEFORE touching the request. The old
      // order updated the status first, so a validation failure still marked the
      // request completed and emailed the employee.
      // Warranty is NOT required here — "No Warranty" is a valid choice.
      if (creatingAccessory) {
        if (!accForm.name?.trim()) { setError('Accessory Name is required to add to module.'); setSaving(false); return; }
        if (!accForm.purchased_date) { setError('Purchase Date is required.'); setSaving(false); return; }
        if (!accForm.purchase_price) { setError('Purchase Price is required.'); setSaving(false); return; }
        const accRes = await api.post('/accessories', {
          ...accForm,
          assigned_to: selected.employee_id || undefined,
        });
        linkedAccessoryId = accRes.data.id;
      }

      // Single status update. This previously ran twice when adding an accessory,
      // sending the employee two identical "Request Completed" emails.
      await api.put(`/accessory-requests/${selected.id}/status`, {
        ...statusForm,
        ...(linkedAccessoryId ? { linked_accessory_id: linkedAccessoryId } : {}),
      });

      setModal(null); setSelected(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Update failed'); } finally { setSaving(false); }
  };

  const columns = [
    { header: 'Req #', render: r => <span className="font-mono text-xs font-semibold" style={{color:'#60a5fa'}}>{r.request_number || '—'}</span> },
    { header: 'Employee', render: r => (
      <div>
        <div className="font-medium text-sm" style={{color: r.employee ? '#e2e8f0' : '#94a3b8'}}>
          {r.employee?.full_name || 'Direct — IT Department'}
        </div>
        <div className="text-xs" style={{color:'#475569'}}>
          {r.employee?.designation || (r.employee ? '' : `Raised by ${r.submittedBy?.full_name || 'IT'}`)}
        </div>
      </div>
    )},
    { header: 'Items Requested', render: r => (
      <div className="text-xs" style={{color:'#94a3b8'}}>
        {[...(r.requested_items || []), r.other_item].filter(Boolean).join(', ') || '—'}
      </div>
    )},
    { header: 'Status', render: r => (
      <div className="space-y-1">
        <StatusBadge status={r.status}/>
        <div><BoardApprovalChip status={r.board_approval_status}/></div>
      </div>
    )},
    { header: 'Submitted', render: r => <span className="text-xs" style={{color:'#334155'}}>{r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB') : '—'}</span> },
    { header: '', render: r => (
      <button onClick={() => openDetail(r)} className="btn-ghost px-2 py-1 text-xs">
        <ClipboardList size={12}/> View
      </button>
    )}
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="IT Accessory Requests" count={total}>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && <CompanyDeptFilter companyId={filterCompany} deptId={filterDept} onChange={(c,d) => { setFilterCompany(c); setFilterDept(d); setPage(1); }}/>}
          {isIT && (
            <button onClick={() => { setObForm({ on_behalf: true, employee_id: '', items: [], other: '', showOther: false, reason: '' }); setObError(''); setModal('onbehalf'); }}
              className="btn-primary"><Plus size={13}/> New Request (on behalf)</button>
          )}
          <button onClick={load} className="btn-ghost"><RefreshCw size={13} className={loading ? 'animate-spin' : ''}/></button>
        </div>
      </PageHeader>

      <FilterBar hasFilters={!!(search||filterStatus||dateFrom||dateTo)} onClear={() => { setSearch(''); setFilterStatus(''); setDateFrom(''); setDateTo(''); setPage(1); }}>
        <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search req #, employee, item..."/>
        <FilterSelect value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }} placeholder="All Statuses" options={STATUSES.map(s => ({value:s.value,label:s.label}))}/>
        <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); setPage(1); }}/>
      </FilterBar>

      {/* Status pills */}
      <div className="flex gap-2 flex-wrap">
        {STATUSES.map(s => {
          const count = rows.filter(r => r.status === s.value).length;
          if (!count && filterStatus !== s.value) return null;
          return (
            <button key={s.value} onClick={() => setFilterStatus(filterStatus === s.value ? '' : s.value)}
              className="text-xs px-3 py-1.5 rounded-full font-medium transition-all"
              style={{ background: filterStatus === s.value ? `${STATUS_COLORS[s.value]}25` : 'rgba(255,255,255,0.04)',
                       color: STATUS_COLORS[s.value], border: `1px solid ${STATUS_COLORS[s.value]}40` }}>
              {s.label}: {count}
            </button>
          );
        })}
      </div>

      <Table columns={columns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage}/>

      {/* Detail / Status Update Modal */}
      {modal === 'detail' && selected && (
        <Modal title={`Request ${selected.request_number || ''} — ${selected.employee?.full_name || '—'}`} onClose={() => { setModal(null); setSelected(null); }} size="lg">
          <div className="space-y-4">
            {/* Request info */}
            <div className="rounded-xl p-4 space-y-2" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.07)'}}>
              <div className="text-xs" style={{color:'#475569'}}>Items: <span style={{color:'#e2e8f0'}}>{[...(selected.requested_items||[]),selected.other_item].filter(Boolean).join(', ')}</span></div>
              <div className="text-xs" style={{color:'#475569'}}>Reason: <span style={{color:'#e2e8f0'}}>{selected.reason}</span></div>
              <div className="text-xs" style={{color:'#475569'}}>Submitted: <span style={{color:'#e2e8f0'}}>{selected.created_at ? new Date(selected.created_at).toLocaleString('en-GB') : '—'}</span></div>
              <div className="text-xs">Status: <StatusBadge status={selected.status}/></div>
            </div>

            {/* ── Board Approval (admin only) — shared component with IT Tickets ── */}
            {isAdmin && !['completed'].includes(selected.status) && (
              <BoardApprovalPanel
                basePath="/accessory-requests"
                recordId={selected.id}
                info={boardInfo}
                itemLabel="request"
                onChange={(info, meta) => {
                  setBoardInfo(info);
                  if (info?.status === 'pending') {
                    setSelected(sel => ({ ...sel, status: 'pending_board_approval', board_approval_status: 'pending' }));
                    setStatusForm(f => ({ ...f, status: 'pending_board_approval' }));
                  } else if (meta?.restoredStatus) {
                    // Request withdrawn — back to the status it had before board approval
                    setSelected(sel => ({ ...sel, status: meta.restoredStatus, board_approval_status: null }));
                    setStatusForm(f => ({ ...f, status: meta.restoredStatus }));
                  }
                  refreshTimeline(selected.id);
                  load();
                }}
              />
            )}

            {/* Event timeline — what happened when */}
            <div className="rounded-xl p-4" style={{background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)'}}>
              <div className="text-xs font-semibold mb-3" style={{color:'#94a3b8'}}>Timeline — what happened when</div>
              <EventTimeline events={timeline} loading={timelineLoading}/>
            </div>

            {canUpdateStatus && (
              <form onSubmit={handleUpdateStatus} className="space-y-3">
                <ErrMsg msg={error}/>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Update Status</label>
                  <select value={statusForm.status||''} onChange={e=>setStatusForm({...statusForm,status:e.target.value})} className="input-dark">
                    {STATUSES.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <FF label="IT Notes (visible to employee)" name="it_notes" form={statusForm} setForm={setStatusForm} rows={2}/>
                {['rejected_by_it','rejected_by_board'].includes(statusForm.status) && (
                  <FF label="Rejection Reason *" name="rejection_reason" form={statusForm} setForm={setStatusForm} rows={2}/>
                )}
                {statusForm.status === 'approved' && (
                  <FF label="Approved By (board/authority name)" name="approved_by" form={statusForm} setForm={setStatusForm}/>
                )}

                {/* Add to Accessories checkbox — only when marking completed */}
                {statusForm.status === 'completed' && (
                  <div className="rounded-xl p-4" style={{background:'rgba(16,185,129,0.06)',border:'1px solid rgba(16,185,129,0.2)'}}>
                    <label className="flex items-center gap-2 cursor-pointer text-sm mb-3" style={{color:'#e2e8f0'}}>
                      <input type="checkbox" checked={addToAccessory} onChange={e => setAddToAccessory(e.target.checked)} className="accent-green-500 w-4 h-4"/>
                      <span className="font-medium" style={{color:'#34d399'}}>Add item(s) to Accessories Module</span>
                    </label>
                    {addToAccessory && (
                      <div className="space-y-3 mt-2">
                        <div className="text-xs mb-2" style={{color:'#334155'}}>Fill in accessory details to register it in inventory. It will be auto-assigned to the requesting employee.</div>
                        <div className="grid grid-cols-2 gap-3">
                          <FF label="Item Name *" name="name" form={accForm} setForm={setAccForm} required placeholder="e.g. HP Laptop Power Adapter"/>
                          <div>
                            <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Category *</label>
                            <select value={accForm.category||''} onChange={e=>setAccForm({...accForm,category:e.target.value})} className="input-dark">
                              <option value="">Select category</option>
                              {categories.map(c=><option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <FF label="Brand" name="brand" form={accForm} setForm={setAccForm}/>
                          <FF label="Serial Number" name="serial_number" form={accForm} setForm={setAccForm}/>
                          <FF label="Purchase Price (LKR) *" name="purchase_price" type="number" form={accForm} setForm={setAccForm} required/>
                          <FF label="Purchase Date *" name="purchased_date" type="date" form={accForm} setForm={setAccForm} required/>
                          <FF label="Vendor" name="vendor" form={accForm} setForm={setAccForm}/>
                          <div>
                            <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Warranty Period *</label>
                            <select value={accForm.warranty_period||'1_year'} onChange={e=>setAccForm({...accForm,warranty_period:e.target.value})} className="input-dark">
                              {WARRANTY_PERIODS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="text-xs px-1" style={{color:'#34d399'}}>
                          ✓ Will be auto-assigned to {selected.employee?.full_name || 'the requesting employee'}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-3">
                  <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
                  <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center"
                    style={{background: STATUS_COLORS[statusForm.status] ? `linear-gradient(135deg,${STATUS_COLORS[statusForm.status]},${STATUS_COLORS[statusForm.status]}aa)` : undefined}}>
                    {saving ? 'Saving...' : 'Update Status'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Modal>
      )}

      {/* ── New request on behalf of an employee (IT staff) ── */}
      {modal === 'onbehalf' && (
        <Modal title="New Accessory Request — On Behalf Of" onClose={() => setModal(null)} size="lg">
          <form onSubmit={submitOnBehalf} className="space-y-4">
            <ErrMsg msg={obError}/>
            <div>
              <label className="block text-xs font-semibold mb-2" style={{color:'#94a3b8'}}>1. Who is this request for? *</label>
              <div className="flex gap-2 mb-2">
                {[
                  [true,  'On behalf of an employee'],
                  [false, 'Direct — IT Department'],
                ].map(([val, label]) => (
                  <button key={String(val)} type="button"
                    onClick={() => setObForm(p => ({ ...p, on_behalf: val, employee_id: val ? p.employee_id : '' }))}
                    className="text-xs px-3 py-1.5 rounded-lg font-medium flex-1"
                    style={{
                      background: obForm.on_behalf === val ? 'rgba(59,130,246,0.18)' : 'rgba(255,255,255,0.04)',
                      color: obForm.on_behalf === val ? '#60a5fa' : '#94a3b8',
                      border: `1px solid ${obForm.on_behalf === val ? 'rgba(59,130,246,0.4)' : 'rgba(255,255,255,0.08)'}`,
                      cursor: 'pointer',
                    }}>
                    {label}
                  </button>
                ))}
              </div>
              {obForm.on_behalf ? (
                <SearchableSelect value={obForm.employee_id} onChange={v => setObForm(p => ({...p, employee_id: v}))}
                  options={employees.map(e => ({ value: e.id, label: `${e.employee_code || ''} — ${e.full_name}`.replace(/^ — /, '') }))}
                  placeholder="Select the employee this request is for…"/>
              ) : (
                <div className="text-xs px-3 py-2 rounded-lg" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)',color:'#64748b'}}>
                  Logged as a direct IT Department request — no employee is linked (e.g. general stock or replacement items).
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold mb-2" style={{color:'#94a3b8'}}>2. Required Accessories *</label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {REQUEST_ITEMS.map(item => (
                  <label key={item} className="flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer"
                    style={{ background: obForm.items.includes(item) ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.03)',
                             border: `1px solid ${obForm.items.includes(item) ? 'rgba(59,130,246,0.35)' : 'rgba(255,255,255,0.06)'}` }}>
                    <input type="checkbox" checked={obForm.items.includes(item)}
                      onChange={e => setObForm(prev => ({ ...prev, items: e.target.checked ? [...prev.items, item] : prev.items.filter(i => i !== item) }))}
                      className="accent-blue-500 w-4 h-4"/>
                    <span className="text-sm" style={{color:'#e2e8f0'}}>{item}</span>
                  </label>
                ))}
                <label className="flex items-center gap-2.5 px-3 py-2 rounded-xl cursor-pointer md:col-span-2"
                  style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                  <input type="checkbox" checked={!!obForm.showOther}
                    onChange={e => setObForm(prev => ({...prev, showOther: e.target.checked, other: e.target.checked ? prev.other : ''}))}
                    className="accent-blue-500 w-4 h-4"/>
                  <span className="text-sm" style={{color:'#e2e8f0'}}>Other</span>
                  {obForm.showOther && (
                    <input type="text" value={obForm.other} onChange={e => setObForm(prev => ({...prev, other: e.target.value}))}
                      placeholder="Describe the item" className="input-dark flex-1 text-xs py-1" style={{marginLeft:8}}/>
                  )}
                </label>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{color:'#94a3b8'}}>3. Reason *</label>
              <textarea value={obForm.reason} onChange={e => setObForm(prev => ({...prev, reason: e.target.value}))}
                rows={2} className="input-dark" placeholder="Why does this employee need these items?"/>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={obSaving} className="btn-primary flex-1 justify-center">{obSaving ? 'Submitting…' : 'Submit Request'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
